import React, { useEffect, useRef, useState } from 'react'; // useState kept for renderError
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import { toPng } from 'html-to-image';
import { useTheme } from '../../context/ThemeProvider';
import { getColorSchemePalette } from '../../context/colorSchemes';
import { useMermaidSvgRegistry } from '../../context/MermaidSvgRegistryContext';
import VizActionBar from '../shared/Viz/VizActionBar';
import VizDownloadButton from '../shared/Viz/VizDownloadButton';
import VizCopyButton from '../shared/Viz/VizCopyButton';
import useNativeFullscreen from '../../hooks/useNativeFullscreen';
import logger from '../../services/Logger';
import { useOpenInNebula } from "../../hooks/useOpenInNebula";
import { getMarkmapRenderPlan } from '../../utils/markmapRenderPlan';

const transformer = new Transformer();

const MarkmapDiagram = ({ chart, hideControls = false, toggleElement = null }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const visibleRef = useRef(null);
  const markmapRef = useRef(null);
  const [renderError, setRenderError] = useState(null);
  const [copiedPng, setCopiedPng] = useState(false);
  const openInNebula = useOpenInNebula();
  const { isFullscreen, enterFullscreen } = useNativeFullscreen(containerRef);
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const registry = useMermaidSvgRegistry();

  useEffect(() => {
    if (!chart || !svgRef.current) return;

    let wheelCleanup = null; // assigned inside the timer, called by the cleanup return
    const scheduledTimers = new Set();
    const schedule = (callback, delay) => {
      const timerId = setTimeout(() => {
        scheduledTimers.delete(timerId);
        callback();
      }, delay);
      scheduledTimers.add(timerId);
      return timerId;
    };

    // Debounce to handle streaming chunks
    schedule(() => {
      try {
        const { root } = transformer.transform(chart);
        const renderPlan = getMarkmapRenderPlan(
          root,
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        );

        // Destroy previous instance if re-rendering
        if (markmapRef.current) {
          markmapRef.current.destroy();
          markmapRef.current = null;
        }

        // Clear SVG before re-render
        svgRef.current.innerHTML = '';

        // d3-zoom's defaultExtent reads width.baseVal.value and throws if the SVG
        // has a relative (%) width attribute. Setting explicit pixel attrs here
        // gives d3 concrete numbers; Markmap will overwrite them with a viewBox anyway.
        const _svgW = svgRef.current.parentElement?.clientWidth || 800;
        svgRef.current.setAttribute('width', _svgW);
        svgRef.current.setAttribute('height', 480);

        const colors = theme === 'dark'
          ? ['#60C8F0', '#4F7AFF', '#A78BFA', '#34D399', '#FB8F8F']   // lighter, less harsh in dark
          : ['#3B6FF5', '#44B8D8', '#7C3AED', '#059669', '#E8624A'];  // desaturated red → warm coral in light
        const colorFn = (node) => colors[node.state.depth % colors.length];

        // Create fully expanded immediately — capture for export registry first,
        // then fold back and run the animated reveal so the user sees the animation.
        const mm = Markmap.create(svgRef.current, {
          autoFit: true,
          color: colorFn,
          duration: 0,
          initialExpandLevel: renderPlan.initialExpandLevel,
        }, root);

        // Collect nodes by depth for sequential expansion animation
        const nodesByDepth = {};
        const collectNodes = (node) => {
          if (!node) return;
          const d = node.state?.depth ?? 0;
          if (!nodesByDepth[d]) nodesByDepth[d] = [];
          nodesByDepth[d].push(node);
          (node.children || []).forEach(collectNodes);
        };
        collectNodes(mm.state.data);

        const depths = Object.keys(nodesByDepth).map(Number).sort((a, b) => a - b);
        // ── Step 1: Capture fully-expanded SVG for export registry (runs early) ──
        if (registry && svgRef.current) {
          schedule(() => {
            if (!svgRef.current) return;
            const svg = svgRef.current;
            const vb = svg.viewBox?.baseVal;
            const svgW = (vb?.width > 0 ? vb.width : null) || svg.clientWidth || 1200;
            const svgH = (vb?.height > 0 ? vb.height : null) || svg.clientHeight || 800;
            const prevW = svg.style.width;
            const prevH = svg.style.height;
            svg.style.width = svgW + 'px';
            svg.style.height = svgH + 'px';
            toPng(svg, {
              cacheBust: true,
              pixelRatio: 2,
              backgroundColor: palette.background,
              width: svgW,
              height: svgH,
              skipFonts: true,
            })
              .then((pngBase64) => {
                registry.registerDiagram(chart, pngBase64);
              })
              .catch(() => {})
              .finally(() => {
                svg.style.width = prevW;
                svg.style.height = prevH;
              });
          }, 400);
        }

        // ── Step 2: Fold all non-root nodes back, then animate reveal ──
        if (renderPlan.animateReveal) schedule(() => {

          // Fold everything except root by toggling nodes that are currently open
          depths.filter(d => d > 0).forEach(depth => {
            nodesByDepth[depth].forEach(node => {
              if (!node.payload?.fold) mm.toggleNode(node, true); // fold
            });
          });
          mm.setOptions({ duration: 0 });
          mm.renderData();

          // Now run the sequential animated expand
          const stepDelay = 2000;
          depths.forEach((depth, i) => {
            schedule(() => {
              if (!mm?.state?.data) return;
              mm.setOptions({ duration: 1500 });
              nodesByDepth[depth].forEach((node) => {
                if (node.payload?.fold) mm.toggleNode(node);
              });
            }, 400 + i * stepDelay);
          });

          // Restore speed and lock open after all levels expand
          schedule(() => {
            if (mm) {
              mm.setOptions({ duration: 300 });
              mm.toggleNode = () => {};
            }
          }, 400 + depths.length * stepDelay + 300);
        }, 600); // after capture has fired

        // ── Interaction: no panning, zoom-to-center only ────────────────────────
        // Two-finger trackpad scroll generates wheel events with deltaX/deltaY.
        // D3-zoom translates the map on those by default. We block ALL wheel events
        // from reaching d3-zoom (capture phase, stopPropagation + preventDefault),
        // then manually apply only the scale change, always keeping the map centered.
        // Mouse drag and touch drag are also blocked via capture-phase mousedown.
        const svgEl = svgRef.current;

        // Block drag (mouse / touch)
        const blockDrag = (e) => e.stopPropagation();
        svgEl.addEventListener('mousedown', blockDrag, true);
        svgEl.addEventListener('touchstart', blockDrag, { capture: true, passive: false });
        svgEl.addEventListener('dblclick', blockDrag, true);

        // Capture the fitted transform scale and viewport size after autoFit settles.
        let k0 = 1, svgW = 0, svgH = 0;
        schedule(() => {
          const t = svgEl.__zoom;
          if (t) k0 = t.k;
          const rect = svgEl.getBoundingClientRect();
          svgW = rect.width || svgEl.clientWidth || 800;
          svgH = rect.height || svgEl.clientHeight || 600;
        }, 300);

        // Apple-Watch-style wheel zoom:
        //   • zooming in → scales up, pinned to the SVG viewport center
        //   • zooming back out past the fit level → snaps back to fit (centered)
        //   • no translation ever — deltaX is fully ignored
        //   • uses mm.zoom.scaleTo() so d3 always holds proper ZoomTransform instances
        //     (plain objects would break .invert() calls inside d3 animations)
        let recenterTimer = null;
        const handleWheelZoom = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!markmapRef.current) return;

          const current = svgEl.__zoom;
          if (!current) return;

          // Sensitivity: pixel mode (0) vs line (1) vs page (2)
          const sensitivity = e.deltaMode === 1 ? 0.1 : e.deltaMode === 2 ? 1 : 0.004;
          const factor = Math.pow(2, -e.deltaY * sensitivity);
          const newK = Math.max((k0 || 1) * 0.5, Math.min((k0 || 1) * 8, current.k * factor));

          // scaleTo(selection, k, point) — scale to k keeping `point` in screen-space fixed.
          // Passing the SVG center means the map's visual center never drifts.
          mm.zoom.scaleTo(mm.svg, newK, [svgW / 2, svgH / 2]);

          // Snap back to centered fit when user zooms back out to fit level
          clearTimeout(recenterTimer);
          if (newK <= (k0 || 1) * 1.05) {
            recenterTimer = setTimeout(() => markmapRef.current?.fit(), 350);
          }
        };
        svgEl.addEventListener('wheel', handleWheelZoom, { capture: true, passive: false });

        wheelCleanup = () => {
          svgEl.removeEventListener('mousedown', blockDrag, true);
          svgEl.removeEventListener('touchstart', blockDrag, { capture: true });
          svgEl.removeEventListener('dblclick', blockDrag, true);
          svgEl.removeEventListener('wheel', handleWheelZoom, { capture: true });
          clearTimeout(recenterTimer);
        };

        markmapRef.current = mm;
        setRenderError(null);
      } catch (err) {
        logger.warn('MarkmapDiagram', 'Markmap rendering failed', { error: err.message });
        setRenderError(err);
      }
    }, 500);

    return () => {
      scheduledTimers.forEach(clearTimeout);
      scheduledTimers.clear();
      wheelCleanup?.();
      if (markmapRef.current) {
        markmapRef.current.destroy();
        markmapRef.current = null;
      }
    };
  }, [chart, theme, palette, registry]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (markmapRef.current) {
        markmapRef.current.destroy();
      }
    };
  }, []);

  // When entering/exiting native fullscreen, resize the SVG to match
  // the new container dimensions and re-fit the Markmap.
  useEffect(() => {
    if (!svgRef.current) return;
    const svgEl = svgRef.current;
    if (isFullscreen) {
      const parent = svgEl.parentElement;
      svgEl.setAttribute('width', parent?.clientWidth || 1200);
      svgEl.setAttribute('height', parent?.clientHeight || 700);
    } else {
      svgEl.setAttribute('width', svgEl.parentElement?.clientWidth || 800);
      svgEl.setAttribute('height', 480);
    }
    const timer = setTimeout(() => markmapRef.current?.fit(), 100);
    return () => clearTimeout(timer);
  }, [isFullscreen]);

  const handleCopyPng = () => {
    if (!visibleRef.current) return;
    toPng(visibleRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true })
      .then((dataUrl) => {
        const base64 = dataUrl.split(',')[1];
        const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'image/png' });
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          .then(() => {
            setCopiedPng(true);
            setTimeout(() => setCopiedPng(false), 2000);
          })
          .catch((err) => logger.warn('MarkmapDiagram', 'Failed to copy PNG', { error: err.message }));
      })
      .catch((err) => logger.warn('MarkmapDiagram', 'Failed to generate PNG for copy', { error: err.message }));
  };

  const handleDownload = () => {
    if (!visibleRef.current) return;
    toPng(visibleRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true })
      .then((dataUrl) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `mindmap-${Date.now()}.png`;
        a.click();
      })
      .catch((err) => {
        logger.warn('MarkmapDiagram', 'Failed to download PNG', { error: err.message });
      });
  };

  if (renderError) {
    return (
      <div className="relative rounded-md overflow-hidden my-3">
        <div className="p-4 bg-fill-gray-50 dark:bg-fill-hex-222222 border border-edge-gray-200 dark:border-edge-editor-reset-border rounded-md font-mono text-xs sm:text-sm text-ink-hex-0077aa dark:text-ink-copy-hover whitespace-pre-wrap break-words">
          {chart}
        </div>
        <div className="absolute top-0 right-0 p-1">
          <span className="text-[10px] text-ink-red-800 bg-fill-red-100 dark:bg-fill-red-900/30 px-1 rounded dark:text-ink-red-300">Render Error</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative ${hideControls || isFullscreen ? 'my-0 w-full h-full flex items-center justify-center' : 'my-4'} group${isFullscreen ? ' bg-fill-white dark:bg-fill-deep-surface' : ''}`}
      ref={containerRef}
    >
      <style>{`
        .markmap path.markmap-link { stroke-linecap: round !important; stroke-linejoin: round !important; shape-rendering: geometricPrecision; }
        .markmap-foreign { -webkit-font-smoothing: antialiased !important; -moz-osx-font-smoothing: grayscale !important; text-rendering: optimizeLegibility !important; }
        .markmap-foreign > div, .markmap-foreign div { -webkit-font-smoothing: antialiased !important; -moz-osx-font-smoothing: grayscale !important; font-weight: 400; letter-spacing: 0.01em; }
      `}</style>
      <div
        ref={visibleRef}
        className={`p-2 bg-fill-white dark:bg-fill-deep-surface border border-edge-gray-200 dark:border-edge-muted-control overflow-hidden${hideControls || isFullscreen ? ' w-full h-full rounded-none border-0' : ' rounded-xl'}${theme === 'dark' ? ' markmap-dark' : ''}`}
        onWheel={(e) => e.stopPropagation()}
      >
        <svg
          ref={svgRef}
          style={{ width: '100%', height: hideControls || isFullscreen ? '100%' : '480px', display: 'block', shapeRendering: 'geometricPrecision' }}
        />
      </div>

      {!isFullscreen && !hideControls && (
        <VizActionBar
          onFullscreen={enterFullscreen}
          leftContent={toggleElement}
          onOpenInNebula={openInNebula}
        >
          <VizCopyButton
            copied={copiedPng}
            onClick={handleCopyPng}
            tooltip="Copy PNG"
          />
          <VizDownloadButton
            onClick={handleDownload}
            tooltip="Download PNG"
          />
        </VizActionBar>
      )}
    </div>
  );
};

export default MarkmapDiagram;
